import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Input,
  Select,
  Space,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  message,
  Tooltip,
  List,
  Avatar,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  GlobalOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  LinkOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import { isAdmin } from "../../utils/roleUtils";

import "./IssuerDetails.css";

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

function IssuerNews() {
  const { cin } = useParams();
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);

  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const baseUrl =
    process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";

  // Fetch news data
  const fetchNewsData = async (page = 1, pageSize = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/news/issuer/${encodeURIComponent(cin)}?page=${
          page - 1
        }&size=${pageSize}`
      );

      if (response.data.statusCode === 200) {
        setNewsData(response.data.data.content || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.data.data.totalElements || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch news data");
      }
    } catch (error) {
      setError("Error fetching news data");
      message.error("Error fetching news data");
      console.error("Error fetching news data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cin) {
      fetchNewsData();
    }
  }, [cin]);

  const handleRefresh = () => {
    fetchNewsData(pagination.current, pagination.pageSize);
  };

  const handleGoBack = () => {
   navigate(-1);
    // navigate(`/d/issuer/${encodeURIComponent(cin)}`);
  };

  const handleTableChange = (pagination) => {
    fetchNewsData(pagination.current, pagination.pageSize);
  };

  // Filtered data based on search and filters
  const filteredData = newsData.filter((item) => {
    const matchesSearch =
      item.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.content?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.source?.toLowerCase().includes(searchText.toLowerCase());

    const matchesSource =
      selectedSource === "all" || item.source === selectedSource;
    const matchesCategory =
      selectedCategory === "all" || item.category === selectedCategory;

    return matchesSearch && matchesSource && matchesCategory;
  });

  // Get unique values for filters
  const sources = [
    ...new Set(newsData.map((item) => item.source).filter(Boolean)),
  ];
  const categories = [
    ...new Set(newsData.map((item) => item.category).filter(Boolean)),
  ];

  // Stats calculation
  const stats = [
    {
      title: "Total News",
      value: pagination.total,
      icon: <GlobalOutlined />,
      color: "#667eea",
    },
    {
      title: "News Sources",
      value: sources.length,
      icon: <GlobalOutlined />,
      color: "#4ecdc4",
    },
    {
      title: "Latest News",
      value:
        newsData.length > 0 ? formatDate(newsData[0]?.publishedDate) : "N/A",
      icon: <ClockCircleOutlined />,
      color: "#ff6b6b",
    },
    {
      title: "Categories",
      value: categories.length,
      icon: <FileTextOutlined />,
      color: "#feca57",
    },
  ];

  const getSourceColor = (source) => {
    const colors = {
      "Economic Times": "blue",
      "Business Standard": "green",
      "Financial Express": "purple",
      LiveMint: "orange",
      MoneyControl: "cyan",
      Reuters: "red",
      Bloomberg: "magenta",
    };
    return colors[source] || "default";
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Financial Results": "green",
      "Rating Updates": "blue",
      "Corporate Actions": "purple",
      "Market News": "orange",
      Regulatory: "red",
      General: "default",
    };
    return colors[category] || "default";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const tableColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: "14px" }}>
            {text || "N/A"}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.content?.substring(0, 100)}...
          </Text>
        </div>
      ),
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (text) => <Tag color={getSourceColor(text)}>{text || "N/A"}</Tag>,
      filters: sources.map((source) => ({ text: source, value: source })),
      onFilter: (value, record) => record.source === value,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text) => (
        <Tag color={getCategoryColor(text)}>{text || "N/A"}</Tag>
      ),
      filters: categories.map((category) => ({
        text: category,
        value: category,
      })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: "Published Date",
      dataIndex: "publishedDate",
      key: "publishedDate",
      render: (text) => (
        <div>
          <Text>{formatDate(text)}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {formatTime(text)}
          </Text>
        </div>
      ),
      sorter: (a, b) => new Date(a.publishedDate) - new Date(b.publishedDate),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Full Article">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => window.open(record.url, "_blank")}
            />
          </Tooltip>
          <Tooltip title="Open Link">
            <Button
              type="text"
              icon={<LinkOutlined />}
              size="small"
              onClick={() => window.open(record.url, "_blank")}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
        <div className="loading-container">
          <Spin size="large" />
          <Text>Loading news data...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
      {/* Header Section */}
      <div className="issuer-details-header">
        <div className="issuer-details-header-content">
          <div className="issuer-details-header-left">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleGoBack}
              type="text"
              className="back-button"
            >
              Back to Issuer Details
            </Button>
            <div className="issuer-details-title-section">
              <Title level={2} className="issuer-details-title">
                News & Updates - {cin}
              </Title>
              <div className="issuer-details-meta">
                <Tag color="blue" icon={<GlobalOutlined />}>
                  Latest Information
                </Tag>
                <Tag color="green">{pagination.total} Total Articles</Tag>
              </div>
            </div>
          </div>
          <div className="issuer-details-header-right">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="overview-stats">
        <Row gutter={[16, 16]}>
          {stats.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card className="stat-card">
                <Statistic
                  title={stat.title}
                  value={stat.value}
                  prefix={stat.icon}
                  valueStyle={{ color: stat.color }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Latest News Preview */}
      {newsData.length > 0 && (
        <Card className="latest-news-card" style={{ marginBottom: 24 }}>
          <Title level={4}>Latest News</Title>
          <List
            itemLayout="horizontal"
            dataSource={newsData.slice(0, 3)}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => window.open(item.url, "_blank")}
                  >
                    Read More
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: getSourceColor(item.source),
                        color: "white",
                      }}
                    >
                      {item.source?.charAt(0)}
                    </Avatar>
                  }
                  title={
                    <div>
                      <Text strong>{item.title}</Text>
                      <br />
                      <Space>
                        <Tag color={getSourceColor(item.source)} size="small">
                          {item.source}
                        </Tag>
                        <Tag
                          color={getCategoryColor(item.category)}
                          size="small"
                        >
                          {item.category}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {formatDate(item.publishedDate)}
                        </Text>
                      </Space>
                    </div>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: "13px" }}>
                      {item.content?.substring(0, 200)}...
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Controls Section */}
      <Card className="controls-card">
        <div className="controls-header">
          <div className="controls-left">
            <Search
              placeholder="Search news by title, content..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />

            <Select
              value={selectedSource}
              onChange={setSelectedSource}
              style={{ width: 150 }}
              placeholder="Filter by Source"
            >
              <Option value="all">All Sources</Option>
              {sources.map((source) => (
                <Option key={source} value={source}>
                  {source}
                </Option>
              ))}
            </Select>

            <Select
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: 150 }}
              placeholder="Filter by Category"
            >
              <Option value="all">All Categories</Option>
              {categories.map((category) => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </div>

          <div className="controls-right">
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => message.info("Export functionality coming soon")}
              >
                Export
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Data Table */}
      <Card className="data-card">
        <Table
          columns={tableColumns}
          dataSource={filteredData}
          rowKey={(record) => `${record.id}-${record.publishedDate}`}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
          scroll={{ x: true }}
          className={isDark ? "ant-table-dark" : ""}
        />
      </Card>
    </div>
  );
}

export default IssuerNews;
